/**
 * Leviathan Web — Carriers
 * Quasar Prime LLC
 *
 * Third-party freight carriers: the haulers used when a load does not go on the
 * distributor's own truck.
 *
 * The Carrier model existed with no controller, no routes and no UI, while
 * Shipment, Lifting and Receipt each recorded their hauler as free text. Free
 * text alone is how a typo becomes a different company — "Gulf Coast Carriers",
 * "Gulf Coast Carrier" and "gulf coast carriers" are three haulers as far as
 * any report is concerned.
 *
 * List and edit in one screen. A carrier is a short record — name, a couple of
 * federal numbers, a contact, an address — and splitting it across a list page,
 * a detail page and a form page would be three navigations to change a phone
 * number.
 */

import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Loader2, Pencil, Plus, Search, Trash2, Truck, X } from 'lucide-react';
import { apiFetch } from '@/utils/api';
import { API_URL } from '@/config/api';
import { parseApiError } from '@/utils/errorHandler';
import { displayName } from '@/utils/personName';

interface Carrier {
  id: string;
  name: string;
  code: string | null;
  mcNumber: string | null;
  dotNumber: string | null;
  contactFirstName: string | null;
  contactLastName: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  isActive: boolean;
  notes: string | null;
  _count?: { freightRates: number };
}

type FormState = Omit<Carrier, 'id' | '_count'>;

const EMPTY: FormState = {
  name: '',
  code: '',
  mcNumber: '',
  dotNumber: '',
  contactFirstName: '',
  contactLastName: '',
  phone: '',
  email: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  isActive: true,
  notes: '',
};

export default function CarriersPage() {
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'contactLastName'>('name');
  const [editing, setEditing] = useState<{ id: string | null; form: FormState } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '200', sortBy });
      if (search.trim()) params.set('search', search.trim());
      const response = await apiFetch(`${API_URL}/api/carriers?${params.toString()}`);
      if (!response.ok) throw new Error(await parseApiError(response));
      const body = await response.json();
      setCarriers(body.data ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load carriers.');
    } finally {
      setLoading(false);
    }
  }, [search, sortBy]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    setError(null);
    try {
      const url = editing.id
        ? `${API_URL}/api/carriers/${editing.id}`
        : `${API_URL}/api/carriers`;
      const response = await apiFetch(url, {
        method: editing.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Empty strings become null so a blank field reads as "not recorded"
        // rather than as an empty value somebody typed.
        body: JSON.stringify(
          Object.fromEntries(
            Object.entries(editing.form).map(([key, value]) => [
              key,
              typeof value === 'string' && value.trim() === '' ? null : value,
            ])
          )
        ),
      });
      if (!response.ok) throw new Error(await parseApiError(response));
      setEditing(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save carrier.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (carrier: Carrier) => {
    setError(null);
    try {
      const response = await apiFetch(`${API_URL}/api/carriers/${carrier.id}`, {
        method: 'DELETE',
      });
      // The API refuses while shipments or freight rates still reference the
      // carrier, and its message names them. Surfacing that verbatim is the
      // point — "cannot delete" alone sends the operator looking.
      if (!response.ok) throw new Error(await parseApiError(response));
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete carrier.');
    }
  };

  return (
    <div className="space-y-6">
      <div className="relative">
        <div className="border-l-4 border-amber pl-6">
          <h1 className="text-4xl font-display font-bold text-steel tracking-wide uppercase">
            Carriers
          </h1>
          <p className="text-text-secondary text-sm font-mono mt-2">
            &gt; Third-party haulers used when a load does not go on our own truck.
          </p>
        </div>
      </div>

      {error && (
        <div className="flex items-start space-x-2 p-4 bg-error-red/10 border border-error-red/50">
          <AlertTriangle className="w-5 h-5 text-error-red flex-shrink-0 mt-0.5" />
          <p className="text-error-red text-sm font-mono">{error}</p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-text-tertiary absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name, code, MC or DOT number, contact"
            className="form-input pl-9"
          />
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as 'name' | 'contactLastName')}
          className="form-input max-w-[220px]"
        >
          <option value="name">Sort by company</option>
          <option value="contactLastName">Sort by contact surname</option>
        </select>
        <button
          type="button"
          onClick={() => setEditing({ id: null, form: { ...EMPTY } })}
          className="flex items-center space-x-2 px-4 py-2 bg-amber hover:bg-amber/90 text-background font-display font-bold uppercase text-sm tracking-wider"
        >
          <Plus className="w-4 h-4" />
          <span>New Carrier</span>
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center space-x-3 py-16">
          <Loader2 className="w-5 h-5 text-amber animate-spin" />
          <span className="text-text-secondary font-mono">Loading carriers…</span>
        </div>
      ) : carriers.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-panel-border">
          <Truck className="w-10 h-10 text-steel mx-auto mb-3" />
          <p className="text-text-tertiary font-mono">
            {search ? 'No carriers match that search.' : 'No carriers on file yet.'}
          </p>
        </div>
      ) : (
        <div className="bg-panel-dark border border-panel-border overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-panel-border">
                {['Carrier', 'MC / DOT', 'Contact', 'Location', 'Status', ''].map((heading) => (
                  <th
                    key={heading}
                    className="px-4 py-3 text-left text-xs font-mono uppercase tracking-wider text-text-tertiary"
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {carriers.map((carrier) => (
                <tr key={carrier.id} className="border-b border-panel-border/50">
                  <td className="px-4 py-3">
                    <div className="text-text-primary">{carrier.name}</div>
                    {carrier.code && (
                      <div className="text-text-tertiary font-mono text-xs">{carrier.code}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-text-secondary">
                    {carrier.mcNumber && <div>MC {carrier.mcNumber}</div>}
                    {carrier.dotNumber && <div>DOT {carrier.dotNumber}</div>}
                    {!carrier.mcNumber && !carrier.dotNumber && (
                      <span className="text-text-tertiary">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-text-secondary text-sm">
                      {displayName({
                        firstName: carrier.contactFirstName,
                        lastName: carrier.contactLastName,
                      })}
                    </div>
                    {carrier.phone && (
                      <div className="text-text-tertiary font-mono text-xs">{carrier.phone}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-text-secondary text-sm">
                    {[carrier.city, carrier.state].filter(Boolean).join(', ') || '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-0.5 text-xs font-mono border ${
                        carrier.isActive
                          ? 'text-green-400 border-green-500/50 bg-green-500/10'
                          : 'text-text-tertiary border-panel-border bg-panel-border/20'
                      }`}
                    >
                      {carrier.isActive ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() =>
                        setEditing({
                          id: carrier.id,
                          form: {
                            name: carrier.name,
                            code: carrier.code ?? '',
                            mcNumber: carrier.mcNumber ?? '',
                            dotNumber: carrier.dotNumber ?? '',
                            contactFirstName: carrier.contactFirstName ?? '',
                            contactLastName: carrier.contactLastName ?? '',
                            phone: carrier.phone ?? '',
                            email: carrier.email ?? '',
                            address: carrier.address ?? '',
                            city: carrier.city ?? '',
                            state: carrier.state ?? '',
                            zip: carrier.zip ?? '',
                            isActive: carrier.isActive,
                            notes: carrier.notes ?? '',
                          },
                        })
                      }
                      className="p-2 text-text-secondary hover:text-amber"
                      title="Edit"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(carrier)}
                      className="p-2 text-text-secondary hover:text-error-red"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-panel-dark border border-panel-border w-full max-w-3xl p-6 my-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-display text-text-primary uppercase tracking-wide">
                {editing.id ? 'Edit Carrier' : 'New Carrier'}
              </h2>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="text-text-secondary hover:text-amber"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Company Name" required>
                <input
                  value={editing.form.name}
                  onChange={(e) =>
                    setEditing({ ...editing, form: { ...editing.form, name: e.target.value } })
                  }
                  className="form-input"
                  placeholder="Gulf Coast Carriers"
                />
              </Field>
              <Field label="Code">
                <input
                  value={editing.form.code ?? ''}
                  onChange={(e) =>
                    setEditing({ ...editing, form: { ...editing.form, code: e.target.value } })
                  }
                  className="form-input"
                  placeholder="GCC"
                />
              </Field>
              <Field label="MC Number">
                <input
                  value={editing.form.mcNumber ?? ''}
                  onChange={(e) =>
                    setEditing({ ...editing, form: { ...editing.form, mcNumber: e.target.value } })
                  }
                  className="form-input"
                />
              </Field>
              <Field label="DOT Number">
                <input
                  value={editing.form.dotNumber ?? ''}
                  onChange={(e) =>
                    setEditing({ ...editing, form: { ...editing.form, dotNumber: e.target.value } })
                  }
                  className="form-input"
                />
              </Field>

              {/* Two fields, because carrier lists sort by contact surname. */}
              <Field label="Contact First Name">
                <input
                  value={editing.form.contactFirstName ?? ''}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      form: { ...editing.form, contactFirstName: e.target.value },
                    })
                  }
                  className="form-input"
                />
              </Field>
              <Field label="Contact Last Name">
                <input
                  value={editing.form.contactLastName ?? ''}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      form: { ...editing.form, contactLastName: e.target.value },
                    })
                  }
                  className="form-input"
                />
                {(editing.form.contactFirstName ?? '').trim() &&
                  !(editing.form.contactLastName ?? '').trim() && (
                    <p className="text-amber text-xs font-mono mt-1">
                      &gt; Last name is required once a first name is given — lists sort by it.
                    </p>
                  )}
              </Field>

              <Field label="Phone">
                <input
                  value={editing.form.phone ?? ''}
                  onChange={(e) =>
                    setEditing({ ...editing, form: { ...editing.form, phone: e.target.value } })
                  }
                  className="form-input"
                />
              </Field>
              <Field label="Email">
                <input
                  type="email"
                  value={editing.form.email ?? ''}
                  onChange={(e) =>
                    setEditing({ ...editing, form: { ...editing.form, email: e.target.value } })
                  }
                  className="form-input"
                />
              </Field>

              <Field label="Address" className="md:col-span-2">
                <input
                  value={editing.form.address ?? ''}
                  onChange={(e) =>
                    setEditing({ ...editing, form: { ...editing.form, address: e.target.value } })
                  }
                  className="form-input"
                />
              </Field>
              <Field label="City">
                <input
                  value={editing.form.city ?? ''}
                  onChange={(e) =>
                    setEditing({ ...editing, form: { ...editing.form, city: e.target.value } })
                  }
                  className="form-input"
                />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="State">
                  <input
                    value={editing.form.state ?? ''}
                    maxLength={2}
                    onChange={(e) =>
                      setEditing({ ...editing, form: { ...editing.form, state: e.target.value } })
                    }
                    className="form-input"
                  />
                </Field>
                <Field label="ZIP">
                  <input
                    value={editing.form.zip ?? ''}
                    onChange={(e) =>
                      setEditing({ ...editing, form: { ...editing.form, zip: e.target.value } })
                    }
                    className="form-input"
                  />
                </Field>
              </div>

              <Field label="Notes" className="md:col-span-2">
                <textarea
                  rows={2}
                  value={editing.form.notes ?? ''}
                  onChange={(e) =>
                    setEditing({ ...editing, form: { ...editing.form, notes: e.target.value } })
                  }
                  className="form-input resize-none"
                />
              </Field>

              <label className="flex items-center space-x-2 md:col-span-2">
                <input
                  type="checkbox"
                  checked={editing.form.isActive}
                  onChange={(e) =>
                    setEditing({ ...editing, form: { ...editing.form, isActive: e.target.checked } })
                  }
                  className="accent-amber"
                />
                <span className="text-text-secondary text-sm font-mono">
                  Active — inactive carriers stop appearing in pickers but keep their history
                </span>
              </label>
            </div>

            <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t border-panel-border">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="px-5 py-2 border border-panel-border text-text-secondary hover:text-amber font-mono uppercase text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving || !editing.form.name.trim()}
                className="px-6 py-2 bg-amber hover:bg-amber/90 text-background font-display font-bold uppercase text-sm tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving…' : 'Save Carrier'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  required,
  className,
  children,
}: {
  label: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
        {label} {required && <span className="text-amber">*</span>}
      </label>
      {children}
    </div>
  );
}
